import React from 'react';
import { useNavigate } from 'react-router-dom';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { getPublicImageUrl } from '../../utils/storage';
import {
  resolveEventTimeZone,
  formatSessionDateInTimeZone,
  formatSessionClockWithZone,
} from '../../utils/sessionTime';
import { formatEventLocationLabel } from '../../utils/eventLocation';
import { resolveCtaText } from '../../utils/landingCta';
import RegistrationFlow from '../Registration/RegistrationFlow';
import { mapEventRecord } from '../Registration/registrationConstants';
import styles from './LandingPage.module.css';

const { FiCalendar, FiClock, FiVideo, FiArrowRight, FiImage } = FiIcons;

function resolveHeroImage(event) {
  const raw = event?.featured_image_url || event?.image_url || '';
  if (!raw) return null;
  if (String(raw).startsWith('http')) return raw;
  return getPublicImageUrl(raw, 'event-images') || raw;
}

/** Map raw event + sessions into hero display fields. */
export function buildHeroView(event, sessions = []) {
  const tz = resolveEventTimeZone(event);
  const firstSession = sessions[0];
  const dateLabel = firstSession?.start_time
    ? formatSessionDateInTimeZone(firstSession.start_time, tz)
    : (event?.start_date ?? event?.date ?? '');
  const timeLabel = firstSession?.start_time
    ? formatSessionClockWithZone(firstSession.start_time, tz)
    : (event?.start_time ?? event?.time ?? '');

  return {
    badgeText: event?.event_type || null,
    title: event?.title ?? '',
    subtitle: event?.subtitle || null,
    description: event?.description ?? '',
    dateLabel: dateLabel || null,
    timeLabel: timeLabel || null,
    locationLabel: formatEventLocationLabel(event) || null,
    ctaText: resolveCtaText(event),
    featuredImageUrl: resolveHeroImage(event),
  };
}

function HeroBadgeAndMeta({ hero }) {
  return (
    <>
      {hero.badgeText && (
        <div className={styles.lpHeroBadge}>{hero.badgeText}</div>
      )}
      <h1 className={styles.lpHeroTitle}>{hero.title}</h1>
      {hero.subtitle && <p className={styles.lpHeroSubtitle}>{hero.subtitle}</p>}
      {hero.description && <p className={styles.lpHeroDescription}>{hero.description}</p>}
      <div className={styles.lpHeroMeta}>
        {hero.dateLabel && (
          <span>
            <SafeIcon icon={FiCalendar} className={styles.lpHeroMetaIcon} aria-hidden />
            {hero.dateLabel}
          </span>
        )}
        {hero.timeLabel && (
          <span>
            <SafeIcon icon={FiClock} className={styles.lpHeroMetaIcon} aria-hidden />
            {hero.timeLabel}
          </span>
        )}
        {hero.locationLabel && (
          <span>
            <SafeIcon icon={FiVideo} className={styles.lpHeroMetaIcon} aria-hidden />
            {hero.locationLabel}
          </span>
        )}
      </div>
    </>
  );
}

function HeroImageCta({ hero, onRegisterClick }) {
  return (
    <section className={styles.lpHeroWrap}>
      <div className={styles.container}>
        <div className={`${styles.lpHero} ${styles.lpHeroImageCta}`}>
          <div className={styles.lpHeroLeft}>
            <HeroBadgeAndMeta hero={hero} />
            <button type="button" className={styles.lpHeroCta} onClick={onRegisterClick}>
              {hero.ctaText}{' '}
              <SafeIcon icon={FiArrowRight} className={styles.lpHeroCtaIcon} aria-hidden />
            </button>
          </div>
          <div className={styles.lpHeroRight}>
            {hero.featuredImageUrl ? (
              <img
                src={hero.featuredImageUrl}
                alt={hero.title}
                className={styles.lpHeroImage}
              />
            ) : (
              <div className={styles.lpHeroImagePlaceholder}>
                <SafeIcon icon={FiImage} aria-hidden />
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function HeroEmbeddedForm({ hero, event, eventId, refSlug }) {
  return (
    <section className={styles.lpHeroWrap}>
      <div className={styles.container}>
        <div className={`${styles.lpHero} ${styles.lpHeroForm}`}>
          <div className={styles.lpHeroLeft}>
            <HeroBadgeAndMeta hero={hero} />
          </div>
          <div className={styles.lpHeroRight}>
            <div className={styles.lpHeroFormCard}>
              <RegistrationFlow
                variant="embedded"
                eventId={eventId}
                event={mapEventRecord(event)}
                refCode={refSlug}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Hero({
  event,
  sessions = [],
  eventId,
  refSlug = null,
  heroStyle = 'form',
}) {
  const navigate = useNavigate();
  const hero = buildHeroView(event, sessions);
  const resolvedEventId = eventId ?? event?.id;
  const registerPath = refSlug
    ? `/register/${resolvedEventId}?ref=${encodeURIComponent(refSlug)}`
    : `/register/${resolvedEventId}`;

  const handleRegisterClick = () => {
    navigate(registerPath);
  };

  if (heroStyle === 'image_cta') {
    return <HeroImageCta hero={hero} onRegisterClick={handleRegisterClick} />;
  }

  return (
    <HeroEmbeddedForm
      hero={hero}
      event={event}
      eventId={resolvedEventId}
      refSlug={refSlug}
    />
  );
}
